import React, { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  Copy,
  Check,
  FileCode,
  MessageSquare,
  Zap,
  Eye,
  Layers,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Target, // New import
  Home, // New import
} from "lucide-react";
import { SITE_WALKTHROUGH } from "../data/siteWalkthrough";

// Helper to map lens IDs to colors
const getLensColor = (id) => {
  const bullet = SITE_WALKTHROUGH.overviewBullets.find((b) => b.id === id);
  return bullet ? bullet.color : "#fff";
};

export default function WebsiteReview({ onNavigate }) {
  // Flatten steps for easy linear navigation
  const allSteps = SITE_WALKTHROUGH.groups.flatMap((g) =>
    g.steps.map((s) => ({ ...s, group: g.label }))
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [copied, setCopied] = useState({ stepId: null, field: null });

  const step = allSteps[currentStepIndex];
  const group = SITE_WALKTHROUGH.groups.find((g) =>
    g.steps.some((s) => s.id === step.id)
  );

  const handleNext = () => {
    if (currentStepIndex < allSteps.length - 1)
      setCurrentStepIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) setCurrentStepIndex((prev) => prev - 1);
  };

  const handleCopy = (field, text) => {
    navigator.clipboard.writeText(text);
    setCopied({ stepId: step.id, field });
    setTimeout(() => setCopied({ stepId: null, field: null }), 2000);
  };

  return (
    <div
      style={{
        maxWidth: "1000px",
        margin: "0 auto",
        animation: "fadeIn 0.3s ease-out",
        paddingBottom: "100px",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          marginBottom: "40px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          paddingBottom: "24px",
        }}
      >
        <h2
          style={{
            fontSize: "32px",
            fontWeight: "800",
            color: "#fff",
            marginBottom: "8px",
          }}
        >
          {SITE_WALKTHROUGH.title}
        </h2>
        <p style={{ color: "#9ca3af", fontSize: "16px", maxWidth: "700px" }}>
          {SITE_WALKTHROUGH.subtitle}
        </p>

        {/* Dynamic Bullets */}
        <div style={{ display: "flex", gap: "24px", marginTop: "24px" }}>
          {SITE_WALKTHROUGH.overviewBullets.map((bullet) => (
            <div
              key={bullet.id}
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                fontSize: "13px",
                color: "#fff",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: bullet.color,
                }}
              />
              <span style={{ fontWeight: "700" }}>{bullet.label}:</span>{" "}
              {bullet.text}
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: "32px",
          alignItems: "start",
        }}
      >
        {/* SIDEBAR NAVIGATION */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {SITE_WALKTHROUGH.groups.map((g) => (
            <div key={g.id}>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "800",
                  color: "#6b7280",
                  textTransform: "uppercase",
                  marginBottom: "12px",
                  letterSpacing: "1px",
                }}
              >
                {g.label}
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                {g.steps.map((s) => {
                  const isActive = s.id === step.id;
                  const isDone =
                    allSteps.findIndex((x) => x.id === s.id) < currentStepIndex;
                  return (
                    <button
                      key={s.id}
                      onClick={() =>
                        setCurrentStepIndex(
                          allSteps.findIndex((x) => x.id === s.id)
                        )
                      }
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        background: isActive
                          ? "rgba(34,197,94,0.1)"
                          : "transparent",
                        border: "none",
                        borderRadius: "6px",
                        color: isActive
                          ? "#22c55e"
                          : isDone
                          ? "#e5e7eb"
                          : "#6b7280",
                        fontSize: "14px",
                        fontWeight: isActive ? "600" : "400",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "all 0.2s",
                      }}
                    >
                      {isActive && (
                        <div
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "#22c55e",
                          }}
                        />
                      )}
                      {s.file.split("/").pop()}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* MAIN CONTENT CARD */}
        <div>
          {/* NAVIGATION BAR */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                fontFamily: "monospace",
                color: "#666",
              }}
            >
              STEP {currentStepIndex + 1} / {allSteps.length}: {group.label}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handlePrev}
                disabled={currentStepIndex === 0}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "8px",
                  padding: "8px",
                  color: currentStepIndex === 0 ? "#444" : "#fff",
                  cursor: currentStepIndex === 0 ? "default" : "pointer",
                }}
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleNext}
                disabled={currentStepIndex === allSteps.length - 1}
                style={{
                  background: "#22c55e",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px",
                  color: "#000",
                  cursor:
                    currentStepIndex === allSteps.length - 1
                      ? "default"
                      : "pointer",
                  opacity: currentStepIndex === allSteps.length - 1 ? 0.5 : 1,
                }}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div
            style={{
              background: "#111",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            {/* FILE HEADER + ACTIONS */}
            <div
              style={{
                padding: "32px",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      padding: "8px",
                      background: "rgba(34,197,94,0.1)",
                      borderRadius: "8px",
                      color: "#22c55e",
                    }}
                  >
                    <FileCode size={20} />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#fff",
                        fontFamily: "monospace",
                      }}
                    >
                      {step.file}
                    </h3>

                    {/* Lens Chips */}
                    {step.lens && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "8px",
                        }}
                      >
                        {step.lens.map((l) => (
                          <span
                            key={l}
                            style={{
                              fontSize: "10px",
                              fontWeight: "800",
                              textTransform: "uppercase",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              background: `${getLensColor(l)}20`,
                              color: getLensColor(l),
                              border: `1px solid ${getLensColor(l)}40`,
                            }}
                          >
                            {l}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ACTION BUTTONS (Preview) */}
                {step.actions && (
                  <div style={{ display: "flex", gap: "8px" }}>
                    {step.actions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => onNavigate && onNavigate(action)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "rgba(255,255,255,0.1)",
                          border: "none",
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: "600",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        {action.label} <ExternalLink size={12} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: "15px",
                  color: "#9ca3af",
                  marginTop: "16px",
                  lineHeight: "1.5",
                }}
              >
                {step.renders}
              </div>

              {/* STRATEGIC ALIGNMENT (New Section) */}
              {(step.houseStage || step.scottIntent) && (
                <div
                  style={{
                    marginTop: "24px",
                    padding: "20px",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                      color: "#22c55e",
                      fontSize: "11px",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}
                  >
                    <Target size={14} /> Strategic Alignment
                  </div>
                  {step.houseStage && (
                    <div
                      style={{
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        color: "#fff",
                      }}
                    >
                      <Home size={14} color="#9ca3af" /> House Stage:{" "}
                      <strong>{step.houseStage}</strong>
                    </div>
                  )}
                  {step.scottIntent && (
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#d1d5db",
                        fontStyle: "italic",
                      }}
                    >
                      "{step.scottIntent}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CONTENT BODY */}
            <div style={{ padding: "32px", display: "grid", gap: "40px" }}>
              {/* MESSAGING STRATEGY */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#22c55e",
                    fontSize: "11px",
                    fontWeight: "800",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    marginBottom: "16px",
                  }}
                >
                  <MessageSquare size={14} /> Messaging Intent
                </div>
                <div style={{ display: "grid", gap: "12px" }}>
                  {step.messagingIntent.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "flex-start",
                        fontSize: "15px",
                        color: "#e5e7eb",
                        lineHeight: "1.5",
                      }}
                    >
                      <div
                        style={{
                          marginTop: "8px",
                          width: "4px",
                          height: "4px",
                          background: "#555",
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      />
                      {m}
                    </div>
                  ))}
                </div>
              </div>

              {/* TALK TRACK (THE GOLD) */}
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "12px",
                  padding: "24px",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}
                  >
                    <Zap size={14} color="#f59e0b" /> Talk Track
                  </div>
                  <button
                    onClick={() => handleCopy("talkTrack", step.talkTrack)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color:
                        copied.stepId === step.id &&
                        copied.field === "talkTrack"
                          ? "#22c55e"
                          : "#666",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "11px",
                      fontWeight: "600",
                    }}
                  >
                    {copied.stepId === step.id &&
                    copied.field === "talkTrack" ? (
                      <Check size={14} />
                    ) : (
                      <Copy size={14} />
                    )}
                    {copied.stepId === step.id && copied.field === "talkTrack"
                      ? "COPIED"
                      : "COPY"}
                  </button>
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    color: "#fff",
                    fontStyle: "italic",
                    lineHeight: "1.6",
                  }}
                >
                  "{step.talkTrack}"
                </div>
              </div>

              {/* OPERATOR LENS: RISK & SUCCESS */}
              {(step.riskIfMissing || step.successCriteria) && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                  }}
                >
                  {step.riskIfMissing && (
                    <div
                      style={{
                        background: "rgba(239, 68, 68, 0.05)",
                        borderRadius: "12px",
                        padding: "20px",
                        border: "1px solid rgba(239, 68, 68, 0.2)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#ef4444",
                          fontSize: "11px",
                          fontWeight: "800",
                          textTransform: "uppercase",
                          marginBottom: "12px",
                        }}
                      >
                        <AlertTriangle size={14} /> Risk If Missing
                      </div>
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#fca5a5",
                          lineHeight: "1.5",
                        }}
                      >
                        {step.riskIfMissing}
                      </p>
                    </div>
                  )}

                  {step.successCriteria && (
                    <div
                      style={{
                        background: "rgba(34, 197, 94, 0.05)",
                        borderRadius: "12px",
                        padding: "20px",
                        border: "1px solid rgba(34, 197, 94, 0.2)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#22c55e",
                          fontSize: "11px",
                          fontWeight: "800",
                          textTransform: "uppercase",
                          marginBottom: "12px",
                        }}
                      >
                        <CheckCircle size={14} /> Success Criteria
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          padding: 0,
                          listStyle: "none",
                          display: "grid",
                          gap: "8px",
                        }}
                      >
                        {step.successCriteria.map((sc, i) => (
                          <li
                            key={i}
                            style={{
                              fontSize: "13px",
                              color: "#86efac",
                              lineHeight: "1.4",
                              display: "flex",
                              gap: "8px",
                              alignItems: "start",
                            }}
                          >
                            <span style={{ opacity: 0.5 }}>•</span> {sc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* TWO COLUMNS: FEELING & UI */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  paddingTop: "24px",
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#9ca3af",
                      fontSize: "11px",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginBottom: "12px",
                    }}
                  >
                    <Eye size={14} /> Client Feels
                  </div>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#d1d5db",
                      lineHeight: "1.6",
                    }}
                  >
                    {step.whatClientFeels}
                  </p>
                </div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#9ca3af",
                      fontSize: "11px",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginBottom: "12px",
                    }}
                  >
                    <Layers size={14} /> UI Cues
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    {step.uiCues &&
                      step.uiCues.map((cue, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: "11px",
                            background: "rgba(255,255,255,0.1)",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            color: "#fff",
                          }}
                        >
                          {cue}
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
